import { useState, useEffect, ChangeEvent, FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ScenarioCharacter } from '../../api/types/scenario';
import { api } from '../../api';
import { CharacterDetailResponse } from '../../api/types';
import styles from './CharacterDetailsModal.module.css';
import { useAuth } from '../../context/AuthContext';

interface CharacterDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  character: ScenarioCharacter | null;
  onCharacterUpdated?: (updatedCharacter: ScenarioCharacter) => void;
}

function CharacterDetailsModal({
  isOpen,
  onClose,
  character,
  onCharacterUpdated
}: CharacterDetailsModalProps): JSX.Element | null {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isClosing, setIsClosing] = useState<boolean>(false);
  const [mounted, setMounted] = useState<boolean>(false);
  const [fullCharacterDetails, setFullCharacterDetails] = useState<CharacterDetailResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [timestamp, setTimestamp] = useState<number>(Date.now());
  
  // Form state
  const [formData, setFormData] = useState({
    display_name: '',
    tagline: '',
    description: '',
    external_description: '',
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');

  // Check if the current user is the author of the character
  const isCurrentUserAuthor = user?.username === fullCharacterDetails?.author;

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  useEffect(() => {
    if (isOpen && character && character.username) {
      fetchCharacterDetails(character.username);
      setIsEditMode(false);
    }
  }, [isOpen, character]);

  // Add a check to prevent edit mode if user is not the author
  useEffect(() => {
    // If edit mode is active but user is not the author, disable edit mode
    if (isEditMode && fullCharacterDetails && user?.username !== fullCharacterDetails.author) {
      setIsEditMode(false);
    }
  }, [isEditMode, fullCharacterDetails, user]);

  useEffect(() => {
    if (fullCharacterDetails) {
      setFormData({
        display_name: fullCharacterDetails.display_name,
        tagline: fullCharacterDetails.tagline || '',
        description: fullCharacterDetails.description || '',
        external_description: fullCharacterDetails.external_description || '',
      });
      setImagePreview(fullCharacterDetails.icon || '');
    }
  }, [fullCharacterDetails]);

  const fetchCharacterDetails = async (username: string) => {
    setIsLoading(true);
    try {
      const characterDetails = await api.characters.get(username);
      setFullCharacterDetails({
        id: characterDetails.id,
        username: username,
        display_name: characterDetails.display_name,
        tagline: characterDetails.tagline,
        greeting: characterDetails.greeting,
        icon: characterDetails.icon,
        description: characterDetails.description,
        external_description: characterDetails.external_description,
        author: characterDetails.author,
        is_mature: characterDetails.is_mature,
        visibility: characterDetails.visibility
      });
    } catch (error) {
      console.error('Failed to fetch character details:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen || !character || !mounted) return null;

  const handleClose = () => {
    if (isSaving) return;
    
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      setIsEditMode(false);
      onClose();
    }, 300); // Match this with CSS animation duration
  };

  const handleViewFullProfile = () => {
    if (character.username) {
      navigate(`/chars/${character.username}`);
      onClose();
    } else {
      console.error('Character username not found:', character);
    }
  };

  const handleViewAuthorProfile = (authorUsername: string) => {
    navigate(`/users/${authorUsername}`);
    onClose();
  };

  const handleEditClick = () => {
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setError('');
    
    // Reset form data to original values
    if (fullCharacterDetails) {
      setFormData({
        display_name: fullCharacterDetails.display_name,
        tagline: fullCharacterDetails.tagline || '',
        description: fullCharacterDetails.description || '',
        external_description: fullCharacterDetails.external_description || '',
      });
      setImagePreview(fullCharacterDetails.icon || '');
      setImageFile(null);
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setImageFile(file);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!character || !character.username) {
      setError('Character information is missing');
      return;
    }
    
    setIsSaving(true);
    setError('');
    
    try {
      const updateData: any = {
        display_name: formData.display_name,
        tagline: formData.tagline,
        description: formData.description,
        external_description: formData.external_description,
      };
      
      if (imageFile) {
        updateData.icon = imageFile;
      }
      
      const updatedCharacter = await api.characters.get(character.username);
      await updatedCharacter.update(updateData);
      
      // Update timestamp to force image refresh in this component
      setTimestamp(Date.now());
      
      // Refresh character details to get the latest data from the server
      await fetchCharacterDetails(character.username);
      
      // Notify parent component about the update with clean data
      if (onCharacterUpdated && fullCharacterDetails) {
        // Just pass the character id and username to trigger a fresh fetch in the parent
        // This ensures we're always using the latest data from the server
        onCharacterUpdated({
          id: character.id,
          username: character.username,
          display_name: formData.display_name,
          tagline: formData.tagline,
          // Pass empty string for icon to force parent to fetch latest from API
          icon: '',
          is_mature: character.is_mature
        });
      }
      
      setIsEditMode(false);
    } catch (error) {
      console.error('Failed to update character:', error);
      setError('Failed to update character. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Add timestamp to image URLs to prevent caching
  const getImageUrlWithTimestamp = (url: string | undefined) => {
    if (!url) return "https://placehold.co/100x100/252525/FFFFFF/png?text=?";
    return `${url}?t=${timestamp}`;
  };

  const modalContent = (
    <div 
      className={`${styles.modalOverlay} ${isClosing ? styles.closing : ''}`} 
      onClick={handleClose}
    >
      <div 
        className={`${styles.characterDetailsModal} ${isClosing ? styles.closing : ''} ${isEditMode ? styles.editMode : ''}`} 
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.modalTopButtons}>
          <button 
            className={styles.closeButton} 
            onClick={handleClose} 
            disabled={isSaving}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>
        
        {isEditMode && isCurrentUserAuthor ? (
          // Edit Mode Content
          <form onSubmit={handleSubmit} className={styles.editForm}>
            <div className={styles.characterContent}>
              <div className={styles.characterImageContainer}>
                <div className={styles.imageUploadContainer}>
                  <img 
                    src={imagePreview || "https://placehold.co/100x100/252525/FFFFFF/png?text=?"}
                    alt="Character preview" 
                    className={styles.characterImage}
                  />
                  <label className={styles.imageUploadOverlay} htmlFor="character-image-upload">
                    <div className={styles.uploadIconWrapper}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <span className={styles.uploadText}>Change</span>
                    <input 
                      id="character-image-upload"
                      type="file" 
                      accept="image/*" 
                      onChange={handleImageChange}
                      className={styles.imageUploadInput}
                      disabled={isSaving}
                    />
                  </label>
                </div>
              </div>
              
              <div className={styles.characterInfo}>
                <div className={styles.formGroup}>
                  <label htmlFor="display_name" className={styles.formLabel}>Name</label>
                  <input
                    type="text"
                    id="display_name"
                    name="display_name"
                    value={formData.display_name}
                    onChange={handleInputChange}
                    className={styles.formInput}
                    required
                    disabled={isSaving}
                  />
                </div>
                
                <div className={styles.formGroup}>
                  <label htmlFor="tagline" className={styles.formLabel}>Tagline</label>
                  <input
                    type="text"
                    id="tagline"
                    name="tagline"
                    value={formData.tagline}
                    onChange={handleInputChange}
                    className={styles.formInput}
                    disabled={isSaving}
                  />
                </div>
                
                <div className={styles.formGroup}>
                  <label htmlFor="description" className={styles.formLabel}>Description</label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    className={styles.formTextarea}
                    rows={5}
                    maxLength={6250}
                    placeholder="How would your Character describe themselves?"
                    disabled={isSaving}
                  />
                  <span className={styles.charCount}>{formData.description.length}/6250</span>
                </div>
                
                <div className={styles.formGroup}>
                  <label htmlFor="external_description" className={styles.formLabel}>
                    External Description
                  </label>
                  <textarea
                    id="external_description"
                    name="external_description"
                    value={formData.external_description}
                    onChange={handleInputChange}
                    className={styles.formTextarea}
                    rows={4}
                    maxLength={256}
                    placeholder="What do other people know about this Character?"
                    disabled={isSaving}
                  />
                  <span className={styles.charCount}>{formData.external_description.length}/256</span>
                </div>
                
                {error && <div className={styles.errorMessage}>{error}</div>}
              </div>
            </div>
            
            <div className={styles.modalFooter}>
              <button 
                type="button"
                className={styles.cancelButton}
                onClick={handleCancelEdit}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button 
                type="submit"
                className={styles.saveButton}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        ) : (
          // View Mode Content
          <>
            <div className={styles.characterContent}>
              <div className={styles.characterImageContainer}>
                <div className={styles.imageUploadContainer}>
                  <img 
                    src={getImageUrlWithTimestamp(fullCharacterDetails?.icon || character?.icon)}
                    alt={character?.display_name} 
                    className={styles.characterImage}
                  />
                </div>
                {fullCharacterDetails?.author && (
                  <div className={styles.createdByContainer}>
                    <span className={styles.createdByLabel}>Created by</span>
                    <button 
                      className={styles.createdByAuthor}
                      onClick={() => handleViewAuthorProfile(fullCharacterDetails.author)}
                    >
                      @{fullCharacterDetails.author}
                    </button>
                  </div>
                )}
              </div>
              
              <div className={styles.characterInfo}>
                <div className={styles.nameContainer}>
                  <h2 className={styles.characterName}>{fullCharacterDetails?.display_name || character.display_name}</h2>
                  {!isEditMode && isCurrentUserAuthor && (
                    <button 
                      className={styles.editButton}
                      onClick={handleEditClick}
                      aria-label="Edit character"
                    >
                      <span>Edit</span>
                    </button>
                  )}
                </div>
                <p className={styles.characterTagline}>{fullCharacterDetails?.tagline || character.tagline}</p>
                
                <div className={styles.characterDetails}>
                  {isLoading ? (
                    <div className={styles.loadingIndicator}>Loading details...</div>
                  ) : (
                    <>
                      {fullCharacterDetails?.description && (
                        <div className={styles.detailSection}>
                          <h4 className={styles.detailTitle}>Description</h4>
                          <p className={styles.detailText}>{fullCharacterDetails.description}</p>
                        </div>
                      )}
                      
                      {fullCharacterDetails?.external_description && (
                        <div className={styles.detailSection}>
                          <h4 className={styles.detailTitle}>
                            External Description
                          </h4>
                          <p className={styles.detailText}>{fullCharacterDetails.external_description}</p>
                        </div>
                      )}
                      
                      {!fullCharacterDetails?.description && !fullCharacterDetails?.external_description && (
                        <div className={styles.detailSection}>
                          <p className={styles.detailText}>
                            View the full profile to see more details about this character.
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
            
            <div className={styles.modalFooter}>
              <button 
                className={styles.viewProfileButton}
                onClick={handleViewFullProfile}
              >
                View Full Profile
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );

  // Use createPortal to render the modal outside the parent component's DOM hierarchy
  return createPortal(
    modalContent,
    document.body
  );
}

export default CharacterDetailsModal; 